import apiClient from '../api/api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  context: string;
}

export interface DocumentSearchRequest {
  search_query: string;
  chat_history?: ChatMessage[];
  all_file_response?: boolean;
}

export interface DocumentSearchResponse {
  success: boolean;
  code: number;
  data: string;
}

export interface KnowledgeBaseFileResponse {
  file_name: string;
  response: string;
  document_store_name: string;
  document_store_id: string;
  file_id: string;
}

export interface KnowledgeBaseResponse {
  success: boolean;
  code: number;
  data: KnowledgeBaseFileResponse[];
}

export interface FolderOption {
  _id: string;
  folder_name: string;
  folder_description?: string;
  document_store_type?: string;
  id: string;
}

export interface FolderDropdownResponse {
  success: boolean;
  code: number;
  data: FolderOption[];
}

export interface SearchHistoryItem {
  _id: string;
  document_store_id: string;
  result_count: number;
  search_query: string;
  createdAt: string;
  search_response_history: number;
  folder_name: string;
}

export interface SearchHistoryListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    Document_search_history_list: SearchHistoryItem[];
  };
}

export interface SearchResponseHistoryItem {
  _id: string;
  search_result: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  id: string;
}

export interface SearchHistoryDetail {
  _id: string;
  document_store_id: string;
  result_count: number;
  search_query: string;
  is_deleted: boolean;
  search_response_history: SearchResponseHistoryItem[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  id: string;
}

export interface SearchHistoryDetailResponse {
  success: boolean;
  code: number;
  data: SearchHistoryDetail;
}

export interface RegenerateSearchResponse {
  success: boolean;
  code: number;
  data: string;
}

// Chat Session Types
export interface ChatSessionItem {
  _id: string;
  document_store_id: string;
  session_title: string;
  is_pinned: boolean;
  last_message_at: string;
  created_by: string;
  createdAt: string;
}

export interface ChatSessionMessage {
  _id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: {
    sources?: string[];
    tokens_used?: number;
    model?: string;
  };
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatSessionDetail {
  _id: string;
  document_store_id: string;
  session_title: string;
  messages: ChatSessionMessage[];
  is_pinned: boolean;
  is_deleted: boolean;
  last_message_at: string;
  created_by: string;
  createdAt: string;
  updatedAt: string;
  pending_job_id?: string;
}

export interface ChatSessionListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    chat_sessions: ChatSessionItem[];
  };
}

export interface ChatSessionDetailResponse {
  success: boolean;
  code: number;
  data: ChatSessionDetail;
}

export interface SendMessageResponse {
  success: boolean;
  code: number;
  data: {
    job_id: string;
    status: 'processing';
    session_title: string;
    user_message: ChatSessionMessage;
  };
}

export interface ChatMessageJobStatus {
  _id: string;
  session_id: string;
  status: 'processing' | 'done' | 'failed';
  user_message_id: string;
  assistant_message_id?: string;
  assistant_content?: string;
  error_message?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface MessageJobStatusResponse {
  success: boolean;
  code: number;
  data: ChatMessageJobStatus;
}

// ==================== Knowledge Base Session Types ====================

export interface KBSessionFileResponse {
  file_name: string;
  file_id: string;
  response: string;
  document_store_name: string;
  document_store_id: string;
}

export interface KBSearchRun {
  _id: string;
  file_responses: KBSessionFileResponse[];
  is_deleted: boolean;
  createdAt: string;
}

export interface KBSession {
  _id: string;
  document_store_id: string;
  session_title: string;
  search_query: string;
  file_ids?: string[];
  run_count: number;
  is_pinned: boolean;
  last_search_at: string;
  created_by: string;
  createdAt: string;
  search_runs: KBSearchRun[];
}

export interface KBSessionListItem {
  _id: string;
  document_store_id: string;
  session_title: string;
  search_query: string;
  run_count: number;
  is_pinned: boolean;
  last_search_at: string;
  created_by: string;
  createdAt: string;
}

export interface KBSessionListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    sessions: KBSessionListItem[];
  };
}

export interface KBSessionDetailResponse {
  success: boolean;
  code: number;
  data: KBSession;
}

export interface KBExecuteResponse {
  success: boolean;
  code: number;
  data: {
    session: KBSession;
    search_run: KBSearchRun;
  };
}

class DocumentSearchService {
  private BASE_URL = `${import.meta.env.VITE_DMS_API}/dms-v2-srv`;

  /**
   * Get folder dropdown list
   */
  async getFolderDropdown(): Promise<FolderDropdownResponse> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/document-store-srv/dropdown/list`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching folder dropdown:', error);
      throw error;
    }
  }

  /**
   * Simple document search
   */
  async documentSearch(
    documentStoreId: string,
    searchQuery: string
  ): Promise<DocumentSearchResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/document-search-srv/document-search?document_store_id=${documentStoreId}`,
        {
          search_query: searchQuery,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in document search:', error);
      throw error;
    }
  }

  /**
   * AI Chat with conversation history
   */
  async aiChatSearch(
    documentStoreId: string,
    searchQuery: string,
    chatHistory: ChatMessage[] = []
  ): Promise<DocumentSearchResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/document-search-srv/document-search?document_store_id=${documentStoreId}`,
        {
          search_query: searchQuery,
          chat_history: chatHistory,
          is_chat_history: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in AI chat search:', error);
      throw error;
    }
  }

  /**
   * Knowledge base search (all file responses)
   */
  async knowledgeBaseSearch(
    documentStoreId: string,
    searchQuery: string
  ): Promise<KnowledgeBaseResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/document-search-srv/document-search?document_store_id=${documentStoreId}`,
        {
          search_query: searchQuery,
          all_file_response: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in knowledge base search:', error);
      throw error;
    }
  }

  /**
   * Get search history list for a document store
   */
  async getSearchHistoryList(
    documentStoreId: string,
    skip: number = 0,
    limit: number = 10
  ): Promise<SearchHistoryListResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/document-search-history-srv/utils/list`,
        {
          skip,
          limit,
          document_store_id: documentStoreId,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching search history list:', error);
      throw error;
    }
  }

  /**
   * Get detailed search history for a specific search query
   */
  async getSearchHistoryDetail(
    searchHistoryId: string
  ): Promise<SearchHistoryDetailResponse> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/document-search-history-srv/${searchHistoryId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching search history detail:', error);
      throw error;
    }
  }

  /**
   * Regenerate search result for a specific query
   */
  async regenerateSearch(
    documentStoreId: string,
    searchHistoryId: string
  ): Promise<RegenerateSearchResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/document-search-history-srv/regenerate-document-search/${documentStoreId}?document_search_history_id=${searchHistoryId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating search:', error);
      throw error;
    }
  }

  // ==================== Chat Session APIs ====================

  /**
   * Create a new chat session
   */
  async createChatSession(
    documentStoreId: string,
    sessionTitle?: string
  ): Promise<ChatSessionDetailResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/chat-session-srv`,
        {
          document_store_id: documentStoreId,
          session_title: sessionTitle,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  }

  /**
   * Get chat session by ID with all messages
   */
  async getChatSessionById(
    sessionId: string
  ): Promise<ChatSessionDetailResponse> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/chat-session-srv/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching chat session:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of chat sessions for a document store
   */
  async getChatSessionList(
    documentStoreId: string,
    skip: number = 0,
    limit: number = 20,
    searchTerm?: string
  ): Promise<ChatSessionListResponse> {
    try {
      const body: any = {
        document_store_id: documentStoreId,
        skip,
        limit,
      };
      if (searchTerm) {
        body.search_term = searchTerm;
      }
      const response = await apiClient.post(
        `${this.BASE_URL}/chat-session-srv/utils/list`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching chat session list:', error);
      throw error;
    }
  }

  /**
   * Update chat session (title, pin/unpin)
   */
  async updateChatSession(
    sessionId: string,
    data: { session_title?: string; is_pinned?: boolean }
  ): Promise<ChatSessionDetailResponse> {
    try {
      const response = await apiClient.patch(
        `${this.BASE_URL}/chat-session-srv/${sessionId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating chat session:', error);
      throw error;
    }
  }

  /**
   * Delete a chat session
   */
  async deleteChatSession(sessionId: string): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.delete(
        `${this.BASE_URL}/chat-session-srv/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  }

  /**
   * Send a message in a chat session and get AI response
   */
  async sendChatMessage(
    sessionId: string,
    message: string,
    documentStoreId: string
  ): Promise<SendMessageResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/chat-session-srv/${sessionId}/message`,
        {
          message,
          document_store_id: documentStoreId,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  /**
   * Poll job status for an async chat message
   */
  async getMessageJobStatus(
    sessionId: string,
    jobId: string
  ): Promise<MessageJobStatusResponse> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/chat-session-srv/${sessionId}/message/status/${jobId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching message job status:', error);
      throw error;
    }
  }

  /**
   * Delete a specific message from a chat session
   */
  async deleteChatMessage(
    sessionId: string,
    messageId: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.delete(
        `${this.BASE_URL}/chat-session-srv/${sessionId}/message/${messageId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting chat message:', error);
      throw error;
    }
  }

  // ==================== Knowledge Base Session APIs ====================

  /**
   * Create a new KB session (also executes the first search)
   */
  async createKBSession(data: {
    document_store_id: string;
    search_query: string;
    file_ids?: string[];
    session_title?: string;
    instructions?: string;
  }): Promise<KBSessionDetailResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/knowledgebase-session-srv`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating KB session:', error);
      throw error;
    }
  }

  /**
   * Get KB session by ID with all search runs
   */
  async getKBSession(sessionId: string): Promise<KBSessionDetailResponse> {
    try {
      const response = await apiClient.get(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching KB session:', error);
      throw error;
    }
  }

  /**
   * List KB sessions (paginated, metadata only)
   */
  async listKBSessions(
    documentStoreId: string,
    skip: number = 0,
    limit: number = 20,
    searchTerm?: string
  ): Promise<KBSessionListResponse> {
    try {
      const body: any = {
        document_store_id: documentStoreId,
        skip,
        limit,
      };
      if (searchTerm) {
        body.search_term = searchTerm;
      }
      const response = await apiClient.post(
        `${this.BASE_URL}/knowledgebase-session-srv/utils/list`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error listing KB sessions:', error);
      throw error;
    }
  }

  /**
   * Update KB session (title / pin)
   */
  async updateKBSession(
    sessionId: string,
    data: { session_title?: string; is_pinned?: boolean }
  ): Promise<KBSessionDetailResponse> {
    try {
      const response = await apiClient.patch(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating KB session:', error);
      throw error;
    }
  }

  /**
   * Delete a KB session (soft delete)
   */
  async deleteKBSession(sessionId: string): Promise<{ success: boolean; data: boolean }> {
    try {
      const response = await apiClient.delete(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting KB session:', error);
      throw error;
    }
  }

  /**
   * Execute a new search run within an existing KB session
   */
  async executeKBSearch(
    sessionId: string,
    documentStoreId: string,
    instructions?: string
  ): Promise<KBExecuteResponse> {
    try {
      const body: any = { document_store_id: documentStoreId };
      if (instructions) {
        body.instructions = instructions;
      }
      const response = await apiClient.post(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}/execute`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error executing KB search:', error);
      throw error;
    }
  }

  /**
   * Regenerate search (re-run stored query as-is)
   */
  async regenerateKBSearch(
    sessionId: string,
    documentStoreId: string
  ): Promise<KBExecuteResponse> {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}/regenerate`,
        { document_store_id: documentStoreId }
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating KB search:', error);
      throw error;
    }
  }

  /**
   * Delete a specific search run from a KB session
   */
  async deleteKBSearchRun(
    sessionId: string,
    runId: string
  ): Promise<KBSessionDetailResponse> {
    try {
      const response = await apiClient.delete(
        `${this.BASE_URL}/knowledgebase-session-srv/${sessionId}/search-run/${runId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting KB search run:', error);
      throw error;
    }
  }
}

export const documentSearchService = new DocumentSearchService();
export default documentSearchService;
